import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState, useAuth0 } from '@auth0/auth0-react';
import { RIDialog } from '../_core/dialog/dialog.component';
import { FetchUserInviteCodeContainer } from '../domains/user-invite-code/fetch/fetch-user-invite-code-form.container';
import { ROUTE_PATHS } from '../../_routes';
import { RequestAccessContainer } from '../domains/message/request-access-form.component.tsx/request-access-form.container';
import { useSearchParams } from 'react-router-dom';

export const useAuth0WithRedirect = () => {
  const { loginWithRedirect, ...rest } = useAuth0();
  const [searchParams] = useSearchParams();
  const signupCode = useMemo(() => searchParams.get('signup'), [searchParams]);

  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [showUserInviteCodeInput, setShowUserInviteCodeInput] = useState(false);

  useEffect(() => {
    if (signupCode && !rest.isLoading && !rest.isAuthenticated) setShowUserInviteCodeInput(true);
  }, [rest.isAuthenticated, rest.isLoading, signupCode]);

  const startAuth0 = useCallback(
    ({ type = 'login', appState, email }: { type?: 'login' | 'signup'; appState?: AppState; email?: string }) => {
      return loginWithRedirect({
        authorizationParams: { screen_hint: type, login_hint: email },
        appState
      });
    },
    [loginWithRedirect]
  );

  const InviteCodeDialog = useCallback(
    () => (
      <RIDialog open={showUserInviteCodeInput} setOpen={setShowUserInviteCodeInput}>
        <FetchUserInviteCodeContainer
          code={signupCode || undefined}
          onBack={() => setShowUserInviteCodeInput(false)}
          onSubmit={({ _id, email }) => {
            setShowUserInviteCodeInput(false);
            startAuth0({
              type: 'signup',
              appState: { returnTo: ROUTE_PATHS.DASHBOARD + '?foundCode=' + _id },
              email
            });
          }}
          onRequestAccess={() => {
            setShowUserInviteCodeInput(false);
            setShowRequestAccess(true);
          }}
        />
      </RIDialog>
    ),
    [showUserInviteCodeInput, signupCode, startAuth0]
  );

  const RequestAccessDialog = useCallback(
    () => (
      <RIDialog open={showRequestAccess} setOpen={setShowRequestAccess}>
        <RequestAccessContainer close={() => setShowRequestAccess(false)} />
      </RIDialog>
    ),
    [showRequestAccess]
  );

  const Dialogs = (
    <>
      <InviteCodeDialog />
      <RequestAccessDialog />
    </>
  );

  return {
    ...rest,
    loginWithRedirect: startAuth0,
    registerWithRedirect: () => setShowUserInviteCodeInput(true),
    Dialogs
  };
};
